import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import type { Session, User } from '@supabase/supabase-js';

type UserContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
};

const UserContext = createContext<UserContextType>({
  user: null,
  session: null,
  loading: true,
  error: null,
  signOut: async () => {},
  refreshSession: async () => {},
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshingSessionRef = React.useRef<boolean>(false);

  // Check if a session is valid and not expired
  const isSessionValid = React.useCallback(
    (session: Session | null): boolean => {
      if (!session) return false;

      // Check if the session has an expiry time
      if (session.expires_at) {
        // Convert expires_at to milliseconds (it's stored as seconds)
        const expiresAt = new Date(session.expires_at * 1000);
        return expiresAt > new Date();
      }

      return true;
    },
    []
  );

  // Function to check for existing session
  const checkSession = React.useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        console.error('Error checking auth session:', error);
        setError(error.message);
        setUser(null);
        setSession(null);
      } else if (data.session && isSessionValid(data.session)) {
        setSession(data.session);
        setUser(data.session.user);
        setError(null);
      } else {
        // Session is invalid or expired
        setUser(null);
        setSession(null);

        // If there was a session but it's expired, log but don't auto-refresh during initial check
        // Auto-refresh will be handled by the interval checker or auth state changes
        if (data.session && !isSessionValid(data.session)) {
          console.log(
            'Session expired during initial check - will be handled by auth state listener'
          );
        }
      }
    } catch (err) {
      console.error('Unexpected error checking auth:', err);
      setError('Failed to check authentication status');
      setUser(null);
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [isSessionValid]);

  // Function to refresh the session
  const refreshSession = React.useCallback(async () => {
    // Prevent multiple refreshes happening at once
    if (refreshingSessionRef.current) return;

    try {
      refreshingSessionRef.current = true;
      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        console.error('Error refreshing session:', error);
        setError(error.message);
        return;
      }

      if (data.session && isSessionValid(data.session)) {
        setSession(data.session);
        setUser(data.session.user);
        setError(null);
      } else {
        // Still no valid session after refresh attempt
        console.log('Failed to obtain a valid session after refresh');
        setUser(null);
        setSession(null);
      }
    } catch (err) {
      console.error('Unexpected error refreshing session:', err);
      setError('Failed to refresh authentication session');
    } finally {
      refreshingSessionRef.current = false;
    }
  }, [isSessionValid]);

  useEffect(() => {
    // Initial session check
    checkSession();

    // Set up auth state change listener
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (_event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);

        if (import.meta.env.DEV) {
          if (_event === 'SIGNED_IN') {
            console.log('User signed in');
          } else if (_event === 'SIGNED_OUT') {
            console.log('User signed out');
          } else if (_event === 'TOKEN_REFRESHED') {
            console.log('Token refreshed');
          }
        }
      }
    );

    // Return cleanup for auth listener
    return () => {
      authListener.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount - checkSession is stable but causes re-renders if included

  // Session monitoring with stable interval (NO session dependency to prevent loops)
  useEffect(() => {
    const isProd =
      import.meta.env.PROD || import.meta.env.MODE === 'production';
    const checkInterval = isProd ? 30 * 60 * 1000 : 5 * 60 * 1000;

    // Only set up interval checking in development mode
    if (!isProd) {
      const sessionCheckInterval = setInterval(() => {
        // Get current session state at time of check (no dependency needed)
        supabase.auth
          .getSession()
          .then(({ data }) => {
            if (data.session) {
              // Check expiry inline to avoid dependency issues
              const expiresAt = data.session.expires_at
                ? new Date(data.session.expires_at * 1000)
                : null;
              const isExpired = expiresAt && expiresAt <= new Date();

              if (isExpired) {
                console.log(
                  'Session expired during interval check, refreshing...'
                );
                // Call refreshSession directly to avoid dependency
                supabase.auth.refreshSession().then(({ error }) => {
                  if (error) {
                    console.error(
                      'Error refreshing session in interval:',
                      error
                    );
                  }
                });
              }
            }
          })
          .catch(err => {
            console.error('Error checking session in interval:', err);
          });
      }, checkInterval);

      return () => {
        clearInterval(sessionCheckInterval);
      };
    }
  }, []); // ✅ NO dependencies = no infinite loops

  const signOut = React.useCallback(async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out');
    }
  }, []);

  const contextValue = React.useMemo(
    () => ({
      user,
      session,
      loading,
      error,
      signOut,
      refreshSession,
    }),
    [user, session, loading, error, signOut, refreshSession]
  );

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
